import { appendHsOwnerIdIfPresentAndAdmin } from './../../../utils/apiUtilis';
import { axiosApiInstancePrivate } from '../index';
import axios, { AxiosError } from 'axios';
import { DealStage, Lead, Note } from '@tassomutuo-backoffice/types';
import { toast } from 'react-toastify';

export interface LeadsResponse {
  id: string;
  length: number;
  total: number;
  results: Lead[];
}

export const getHsOwnerId = async (
  email: string
): Promise<{
  alert: boolean | false;
  updateAlertDate: string | null;
  hsOwnerId: string;
  email: string;
}> => {
  try {
    const response = await axiosApiInstancePrivate.get<{
      hsOwnerId: string;
      email: string;
      alert: boolean | false;
      updateAlertDate: string | null;
    }>(`/v1/hs/owner-id/${email}`);

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getLeadsUnderManagement = async (): Promise<LeadsResponse> => {
  try {
    const response = await axiosApiInstancePrivate.get<LeadsResponse>(
      appendHsOwnerIdIfPresentAndAdmin('/v1/hs/deals-under-management')
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getDropRequests = async (): Promise<LeadsResponse> => {
  try {
    const response = await axiosApiInstancePrivate.get<LeadsResponse>(
      appendHsOwnerIdIfPresentAndAdmin('/v1/hs/drop-requests')
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const deleteDropRequest = async (
  leadId: string
): Promise<LeadsResponse> => {
  try {
    const response = await axiosApiInstancePrivate.get<LeadsResponse>(
      appendHsOwnerIdIfPresentAndAdmin(`/v1/hs/drop-request-confirm/${leadId}`)
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getLeadsClosed = async (): Promise<LeadsResponse> => {
  try {
    const response = await axiosApiInstancePrivate.get<LeadsResponse>(
      appendHsOwnerIdIfPresentAndAdmin('/v1/hs/deals-closed')
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};
export const getLeadsDropped = async (): Promise<LeadsResponse> => {
  try {
    const response = await axiosApiInstancePrivate.get<LeadsResponse>(
      appendHsOwnerIdIfPresentAndAdmin('/v1/hs/deals-dropped')
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getLeadDetails = async (leadId: string): Promise<Lead> => {
  try {
    const response = await axiosApiInstancePrivate.get<Lead>(
      appendHsOwnerIdIfPresentAndAdmin(`/v1/hs/deal/${leadId}`)
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const updateLead = async (leadData: {
  leadId: string;
  currentDealStage?: DealStage;
  dealStage?: DealStage;
  loanAmount?: string;
  propertyValue?: string;
  immobile_trovato?: string;
  note?: string;
  droppedMotivation?: string;
  dealName: string;
  appointmentTimestamp?: number;
  newAppointmentTimestamp?: number;
  appointmentTime?: string;
  appointmentLocation?: string;
  attoDateTimestamp?: number;
  commission?: string;
  research?: string;
  salesManagerEmail?: string;
  actionType: 'updateStatus' | 'updateAppointment' | 'updateLoanValues';
  meeting_done?: string;
  why_dropped?: string;
  confirmDrop?: boolean;
  trattativa_voucher?: string;
}): Promise<void> => {
  const {
    leadId,
    dealStage,
    currentDealStage,
    loanAmount,
    propertyValue,
    immobile_trovato,
    note,
    droppedMotivation,
    dealName,
    appointmentTimestamp,
    newAppointmentTimestamp,
    appointmentTime,
    appointmentLocation,
    research,
    attoDateTimestamp,
    commission,
    salesManagerEmail,
    actionType,
    meeting_done,
    why_dropped,
    confirmDrop = false,
    trattativa_voucher,
  } = leadData;
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      appendHsOwnerIdIfPresentAndAdmin(`/v1/hs/update/${leadId}`),
      {
        dealStage,
        currentDealStage,
        loanAmount,
        propertyValue,
        immobile_trovato,
        note,
        droppedMotivation,
        dealName,
        appointmentTimestamp,
        newAppointmentTimestamp,
        appointmentTime,
        appointmentLocation,
        attoDateTimestamp,
        commission,
        research,
        salesManagerEmail,
        actionType,
        why_dropped,
        meeting_done: meeting_done || null,
        confirmDrop,
        trattativa_voucher,
      }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getLeadNotes = async (leadId: string): Promise<Note[]> => {
  try {
    const response = await axiosApiInstancePrivate.get<{ results: Note[] }>(
      `/v1/hs/notes/${leadId}`
    );

    return response.data?.results || [];
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const needUpdateNotify = async (lead: object): Promise<void> => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      appendHsOwnerIdIfPresentAndAdmin(`/v1/hs/notify-owner`),
      {
        lead,
      }
    );
    toast.success('Notifica inviata con successo.');
    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    toast.error('Non è stato possibile inviare la notifica.');
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const needUpdateNotifyMany = async (leads: object[]): Promise<void> => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      appendHsOwnerIdIfPresentAndAdmin(`/v1/hs/notify-many-owner`),
      {
        leads,
      }
    );
    toast.success('Notifica inviata con successo.');
    return response.data;
  } catch (err) {
    toast.error('Non è stato possibile inviare la notifica.');
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};
